/**
 * Frontend Error Logging Service
 * Centralized error logging for frontend application
 */

class ErrorLogger {
    constructor() {
        this.errorQueue = [];
        this.maxQueueSize = 50;
        this.flushInterval = 30000; // 30 seconds
        this.apiEndpoint = process.env.REACT_APP_ERROR_LOG_ENDPOINT || '/api/errors/log';
        
        // Flush errors periodically
        if (this.flushInterval > 0) {
            setInterval(() => this.flush(), this.flushInterval);
        }
        
        // Flush errors before page unload
        window.addEventListener('beforeunload', () => {
            this.flush(true); // Synchronous flush
        });
    }
    
    /**
     * Log error
     */
    log(level, message, error = null, context = {}) {
        const errorEntry = {
            timestamp: new Date().toISOString(),
            level,
            message,
            error: error ? {
                message: error.message,
                stack: error.stack,
                name: error.name,
                code: error.code
            } : null,
            context: {
                ...context,
                url: window.location.href,
                userAgent: navigator.userAgent,
                userId: this.getUserId()
            }
        };
        
        // Add to queue
        this.errorQueue.push(errorEntry);
        
        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console[level.toLowerCase()](errorEntry);
        }
        
        // Flush if queue is full
        if (this.errorQueue.length >= this.maxQueueSize) {
            this.flush();
        }
    }
    
    /**
     * Log error
     */
    error(message, error = null, context = {}) {
        this.log('ERROR', message, error, context);
    }
    
    /**
     * Log warning
     */
    warn(message, context = {}) {
        this.log('WARN', message, null, context);
    }
    
    /**
     * Log info
     */
    info(message, context = {}) {
        this.log('INFO', message, null, context);
    }
    
    /**
     * Log API error
     */
    apiError(url, method, status, error, requestData = null) {
        this.error('API request failed', error, {
            apiUrl: url,
            method,
            statusCode: status,
            requestData
        });
    }
    
    /**
     * Get user ID from Redux store or localStorage
     */
    getUserId() {
        try {
            // Try to get from localStorage
            const loginData = localStorage.getItem('loginData');
            if (loginData) {
                const parsed = JSON.parse(loginData);
                if (parsed && parsed.length > 0 && parsed[0].employee_id) {
                    return parsed[0].employee_id;
                }
            }
        } catch (e) {
            // Ignore errors
        }
        return null;
    }
    
    /**
     * Flush error queue to server
     */
    async flush(sync = false) {
        if (this.errorQueue.length === 0) {
            return;
        }
        
        const errorsToSend = [...this.errorQueue];
        this.errorQueue = [];
        
        if (sync) {
            // Synchronous send (for beforeunload)
            try {
                navigator.sendBeacon(
                    `${process.env.REACT_APP_API_URL || ''}${this.apiEndpoint}`,
                    JSON.stringify({ errors: errorsToSend })
                );
            } catch (e) {
                console.error('Failed to send errors via beacon:', e);
            }
        } else {
            // Asynchronous send
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}${this.apiEndpoint}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ errors: errorsToSend })
                });
                
                if (!response.ok) {
                    console.error('Failed to send error logs to server');
                }
            } catch (e) {
                // If sending fails, re-add errors to queue (up to max size)
                this.errorQueue = [...errorsToSend, ...this.errorQueue].slice(0, this.maxQueueSize);
                console.error('Error sending logs:', e);
            }
        }
    }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

// Export instance and class
export default errorLogger;
export { ErrorLogger };
